import React from "react";
import {
  Form,
  InputNumber,
  Switch,
  Typography,
  Divider,
  Row,
  Col,
  Tooltip,
  FormInstance,
} from "antd";
import { ConfigurationBuilderInput } from "../../../../services/client";
import { NamePath } from "antd/es/form/interface";

const { Title, Text } = Typography;

type OptimizationConfigurationFormProps = {
  form: FormInstance<ConfigurationBuilderInput>;
  markFormDirty: (() => void) | null;
};

const useFormWatch = (
  field: NamePath<ConfigurationBuilderInput>,
  form: FormInstance<ConfigurationBuilderInput>,
) => Form.useWatch(field, form);

const field = {
  costMinimizationActive: [
    "optimization_configuration",
    "cost_minimization_active",
  ],
  peakMinimizationActive: [
    "optimization_configuration",
    "peak_minimization",
    "active",
  ],
  peakLimit: ["optimization_configuration", "peak_minimization", "peak_limit"],
} as const satisfies Record<string, NamePath<ConfigurationBuilderInput>>;

const getCostMinimizationGuidingText = (
  isActive: boolean,
  hasContract: boolean,
): React.ReactNode => {
  if (isActive && hasContract) {
    return "Optimizing using the energy contract.";
  } else if (isActive && !hasContract) {
    return (
      <>
        Optimizing on day ahead prices in <strong>Belgium</strong>. To use a
        different bidding zone or a different type of contract, configure the
        energy contract of the site.
      </>
    );
  } else if (!isActive && hasContract) {
    return "Not using the energy contract for cost optimization.";
  } else {
    return "Not optimizing on energy prices. This can be because the site doesn't need it or e.g. the site has a fixed energy price contract so there is no optimization possible.";
  }
};

const OptimizationConfigurationForm: React.FC<
  OptimizationConfigurationFormProps
> = ({ form, markFormDirty }) => {
  const costMinimizationActive = useFormWatch(
    field.costMinimizationActive,
    form,
  );
  const peakMinimizationActive = useFormWatch(
    field.peakMinimizationActive,
    form,
  );

  const unset = (fields: NamePath<ConfigurationBuilderInput>[]) => {
    form.setFields(fields.map((field) => ({ name: field, value: undefined })));
  };

  return (
    <>
      <Title level={3}>Optimization Configuration</Title>

      <div className="bg-gray-50 p-4 rounded-md border-gray-100 border border-solid mb-4">
        <Divider>Peak Minimization</Divider>
        <Row gutter={16}>
          <Col span={4}>
            <Tooltip title="Activate or deactivate peak minimization">
              <Form.Item
                name={field.peakMinimizationActive}
                label="Active"
                valuePropName="checked"
              >
                <Switch
                  onChange={(newValue) => {
                    unset([field.peakLimit]);
                    form.setFieldValue(field.peakMinimizationActive, newValue);
                  }}
                />
              </Form.Item>
            </Tooltip>
          </Col>

          <>
            <Col span={10}>
              <Tooltip title="Specify the peak limit above which the peak price is applied">
                <Form.Item
                  name={field.peakLimit}
                  label="Peak Limit"
                  rules={[
                    {
                      required: peakMinimizationActive,
                      message: "Please enter the peak limit!",
                    },
                    {
                      warningOnly: true,
                      validator: (_, value) => {
                        if (value == null) {
                          return Promise.resolve();
                        }
                        const kW = value / 1000;
                        if (kW < 10 || kW > 10000) {
                          return Promise.reject(
                            new Error(
                              "Peak limit is typically between 10 kW and 10,000 kW (10 MW). Please verify your input to make sure it is correctly entered in kW.",
                            ),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  normalize={(value) => (value != null ? value * 1000 : value)}
                  getValueProps={(value) => ({
                    value: value != null ? value / 1000 : value,
                  })}
                >
                  <InputNumber
                    disabled={!peakMinimizationActive}
                    placeholder="Peak Limit"
                    addonAfter="kW"
                    min={0}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Tooltip>
            </Col>
          </>
        </Row>
        <Divider>Cost Minimization</Divider>
        <Row gutter={16} className="mb-6">
          <Col span={4}>
            <Tooltip title="Enable cost minimization for electricity offtake">
              <Form.Item
                name={field.costMinimizationActive}
                label="Active"
                valuePropName="checked"
                className="mb-0"
              >
                <Switch
                  onChange={(newValue) => {
                    markFormDirty?.();
                    form.setFieldValue(field.costMinimizationActive, newValue);
                  }}
                />
              </Form.Item>
            </Tooltip>
          </Col>
          <Col span={20} className="flex items-center">
            <Text type="secondary">
              {getCostMinimizationGuidingText(
                costMinimizationActive,
                form.getFieldValue(["contract"]) != null,
              )}
            </Text>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default OptimizationConfigurationForm;
